import DocViewer, { DocViewerRenderers } from '@cyntler/react-doc-viewer';
import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { Building, Calendar, NoteIcon } from 'assets/icons';
import ModalBody from 'components/modal/components/body';
import Modalheader from 'components/modal/components/header';
import RemunerationSummary from 'components/remunerationSummary/RemunerationSummary';
import useModal from 'components/modal/useModal';
import UseContext from 'hooks/useContext';
import ApiDatabase from 'server';
import { dateWordShort } from 'helper/helper';
import { POST_IT_TITLE_COLOR } from 'components/postIt/PostIt';

/**
 * Reading a contract without leaving the mission (JBWEB-1208).
 *
 * The document on one side, the terms it was drawn from on the other: the
 * point is to check one against the other, which a contract opened on its own
 * does not let you do.
 *
 * Each party signs their own copy, so the eye that opened this named which one.
 */

// What the viewer takes out of the pane before it draws a page: it renders one
// 100px narrower than its container, under a 50px header and a 46px control
// bar, with 20px of margin above and below.
const VIEWER_PAGE_MARGIN = 100;
const VIEWER_CHROME = 136;
// The viewer scales a page to the width of its pane, so a portrait one runs
// past the bottom. Contracts come out of BeSTT in A4; a page in any other
// shape is shorter than this and simply lands under the fold, never past it.
const A4_PORTRAIT_RATIO = 1.4142;
const ModalContractPreview = () => {
  const { toggle } = useModal();
  const { modalData } = UseContext();
  const [docs, setDocs] = useState([]);
  const [isWaiting, setIsWaiting] = useState(true);
  const [error, setError] = useState(null);
  const token = localStorage.getItem('xsrfToken');

  const [company, setCompany] = useState(null);

  const documentPane = useRef(null);
  const [fitZoom, setFitZoom] = useState(null);

  // Read once, before the viewer exists: it takes its zoom from its initial
  // state and never looks at the prop again, so the value has to be ready by
  // the time the document is.
  useLayoutEffect(() => {
    const pane = documentPane.current;
    if (!pane) return;

    const { width, height } = pane.getBoundingClientRect();
    const pageWidth = width - VIEWER_PAGE_MARGIN;
    const available = height - VIEWER_CHROME;
    if (pageWidth <= 0 || available <= 0) return;

    const zoom = available / (pageWidth * A4_PORTRAIT_RATIO);
    // Never enlarged: a page that already fits is left as it is.
    setFitZoom(Math.min(1, Math.max(0.3, Math.round(zoom * 100) / 100)));
  }, []);

  const mission = modalData?.mission;
  // The mission's own company projection carries the post-it and the names,
  // but not the remuneration terms: fetched here rather than added to the
  // mission detail's aggregation, which every mission view pays for and this
  // modal is opened on demand.
  const missionCompany = mission?.company;
  const tempWorker = mission?.user_assigned_infos;

  useEffect(() => {
    if (!mission?._id || !modalData?.party) return;

    setIsWaiting(true);
    setError(null);
    // The name is derived server-side from the mission, the avenant and the
    // party: the browser says which contract it wants, not what it is called.
    ApiDatabase.getMissionContractFile(
      {
        token,
        idMission: mission._id,
        party: modalData.party,
        avenantNumber: modalData.avenantNumber ?? 0,
        signed: Boolean(modalData.signed),
      },
      (data) => {
        setIsWaiting(false);
        const urls = Array.isArray(data?.urls) ? data.urls : [];
        if (urls.length === 0) {
          setError(
            'Aucun document pour ce contrat — il n’a probablement pas encore été généré.'
          );
          return;
        }
        setDocs(
          urls.filter(Boolean).map((item) => ({
            uri: item.url || item.uri,
            fileName: data.fileName,
            fileType: item.type || 'application/pdf',
          }))
        );
      },
      (err) => {
        setIsWaiting(false);
        if (err.name === 'CanceledError') return;
        console.error(err);
        setError(
          err?.response?.data?.message ?? "Le contrat n'a pas pu être ouvert."
        );
      }
    );
  }, [mission?._id, modalData?.party, modalData?.avenantNumber, modalData?.signed, token]);

  useEffect(() => {
    if (!missionCompany?._id) return;
    ApiDatabase.getCompanyInfo(
      { token, idCompany: missionCompany._id },
      (data) => setCompany(data.company),
      (err) => {
        if (err.name !== 'CanceledError') console.error(err);
      }
    );
  }, [missionCompany?._id, token]);

  const postIt = missionCompany?.post_it;

  return (
    <>
      <Modalheader hide={toggle} hideShow={true}>
        {modalData?.title ?? 'Contrat'}
      </Modalheader>
      <ModalBody>
        {/* The modal is the height of the window, so the two halves fill it and
            scroll on their own: the document stays put while the terms it is
            checked against are read. */}
        <div className={'flex flex-col lg:flex-row gap-4 lg:h-full lg:min-h-0'}>
          {/* The document keeps the larger half: it is what the reader came
              for, and a contract set too narrow is a contract nobody reads. */}
          <div className={'lg:w-3/5 min-w-0 lg:h-full'} ref={documentPane}>
            {error ? (
              <div className={'text-red-500'}>{error}</div>
            ) : isWaiting ? (
              <div className={'animate-pulse h-[70vh] lg:h-full bg-gray-100 rounded'} />
            ) : (
              <DocViewer
                documents={docs}
                // The viewer fills the half it was given rather than a height
                // of its own: a fixed one either overflowed the modal or left
                // a band of white under the page.
                style={{ height: '100%', minHeight: '70vh' }}
                config={{ pdfZoom: { defaultZoom: fitZoom ?? 1 } }}
                pluginRenderers={DocViewerRenderers}
              />
            )}
          </div>

          <div className={'lg:w-2/5 flex flex-col gap-3 min-w-0 lg:h-full lg:overflow-y-auto'}>
            <div className={'border rounded-lg p-4 flex flex-col gap-2'}>
              <div className={'font-semi-bold text-gray-900'}>
                {mission?.title}
                {mission?.id_mission ? ` n°${mission.id_mission}` : ''}
              </div>
              {mission?.duration?.datestart && (
                <div className={'flex items-center gap-2 text-gray-500'}>
                  <Calendar wh={18} color={'#6B7280'} />
                  <span>
                    {dateWordShort(mission.duration.datestart)} &gt;{' '}
                    {dateWordShort(mission.duration.dateend)}
                  </span>
                </div>
              )}
              {missionCompany && (
                <div className={'flex items-center gap-2 text-gray-500'}>
                  <Building wh={18} color={'#6B7280'} />
                  <span className={'truncate'}>
                    {missionCompany.reason_social}
                    {missionCompany.name_commercial &&
                    missionCompany.name_commercial !== missionCompany.reason_social
                      ? ` => ${missionCompany.name_commercial}`
                      : ''}
                    {missionCompany.id_bestt ? ` n°${missionCompany.id_bestt}` : ''}
                  </span>
                </div>
              )}
              {tempWorker && (
                <div className={'border rounded p-3 flex flex-col gap-0.5'}>
                  <div className={'text-gray-900'}>
                    {[tempWorker.firstname, tempWorker.lastname]
                      .filter(Boolean)
                      .join(' ')}
                    {tempWorker.id_bestt ? (
                      <span className={'text-gray-400 ft-sm'}>
                        {' '}
                        N°{tempWorker.id_bestt}
                      </span>
                    ) : null}
                  </div>
                  {tempWorker.email && (
                    <div className={'text-gray-500 break-words'}>
                      {tempWorker.email}
                    </div>
                  )}
                  {tempWorker.phone && (
                    <div className={'text-gray-500'}>{tempWorker.phone}</div>
                  )}
                </div>
              )}
            </div>

            {postIt?.content?.trim() && (
              <div className={'bg-[#FFF3B0] border border-[#F0E18C] rounded-lg p-3 flex flex-col gap-1'}>
                <div className={'flex gap-1.5 items-center text-[#8D7F3C] ft-sm uppercase'}>
                  <NoteIcon wh={16} color={POST_IT_TITLE_COLOR} />
                  <span>
                    Post-it
                    {missionCompany?.name_commercial ||
                    missionCompany?.reason_social
                      ? ` · ${missionCompany.name_commercial ?? missionCompany.reason_social}`
                      : ''}
                  </span>
                </div>
                <div className={'text-[#5C4D0A] whitespace-pre-line break-words'}>
                  {postIt.content}
                </div>
              </div>
            )}

            <RemunerationSummary companyInfo={company} />
          </div>
        </div>
      </ModalBody>
    </>
  );
};

export default ModalContractPreview;
